import { metaData, sys } from '@/constants';
import { BlogDetailsPageCollection, Card, ImageAsset, MultiCardV2CardsCollection } from '@/graphql/__generated/sdk';
import { BrandThemeConfig } from '@/types';

export const getScrollOffset = ({
	navFixedOffsetByPx,
	topOffset,
	targetHeight,
	isSmallScreen = false,
}: {
	navFixedOffsetByPx: number; // desktop usually around 16px // mobile 8px
	topOffset: number; // usually Site Header height
	targetHeight: number; // height of the side navigation
	isSmallScreen: boolean;
}) => {
	if (isSmallScreen) return -(navFixedOffsetByPx + topOffset + targetHeight);
	return -topOffset;
};

export const updateNavPosition = ({
	containerRef,
	targetRef,
	topOffset,
	navFixedOffsetByRem,
	setTargetHeight,
	setIsNavTopThreshold,
	setIsNavBottomLimit,
}: {
	containerRef: React.RefObject<HTMLDivElement>;
	targetRef: React.RefObject<HTMLDivElement>;
	topOffset: number;
	navFixedOffsetByRem: number;
	setTargetHeight: React.Dispatch<React.SetStateAction<number>>;
	setIsNavTopThreshold: React.Dispatch<React.SetStateAction<boolean>>;
	setIsNavBottomLimit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
	if (containerRef.current && targetRef.current) {
		const scrollPosition = window.scrollY + topOffset;
		const container = containerRef.current.parentElement ?? containerRef.current;
		const containerBounds = container.getBoundingClientRect();
		const containerOffsetTop = containerBounds.top;
		const containerOffsetBottom = containerBounds.bottom;
		const containerTop = containerOffsetTop + window.scrollY - topOffset;
		const containerBottom = containerOffsetBottom + window.scrollY;
		const targetBounds = targetRef.current.getBoundingClientRect();
		setTargetHeight(targetBounds.height);
		const bottomLimit = containerBottom - targetBounds.height - navFixedOffsetByRem * 16;
		setIsNavTopThreshold(window.scrollY >= containerTop);
		setIsNavBottomLimit(scrollPosition >= bottomLimit);
	}
};

export const getNewTopPosition = (target: HTMLElement | HTMLHeadingElement, offset: number) => {
	return target.getBoundingClientRect().top + window.scrollY + offset;
};

export const emptyMetaData = {
	_id: 'contentfulMetadataId1',
	contentfulMetadata: {
		...metaData,
	},
	sys: { ...sys },
};

export const NUMBER_OF_RELATED_BLOGS = 3;
export const SKIP_RELATED_BLOGS = 0;
const BLOG_DESCRIPTION_MOBILE_LIMIT = 60;
const BLOG_DESCRIPTION_DESKTOP_LIMIT = 120;

export const mapRelatedBlogs = (
	{ items, limit, skip, total }: BlogDetailsPageCollection,
	config: BrandThemeConfig,
	isSmallScreen: boolean
): MultiCardV2CardsCollection => {
	return {
		__typename: 'MultiCardV2CardsCollection',
		items: items.map((blog) => {
			if (!blog) return null;

			const { sys, contentfulMetadata, ...rest } = blog;
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
			const blogDescription = rest.summary?.json?.content[0]?.content[0]?.value as string;
			const descriptionLimit = isSmallScreen ? BLOG_DESCRIPTION_MOBILE_LIMIT : BLOG_DESCRIPTION_DESKTOP_LIMIT;

			const card: Card = {
				__typename: 'Card',
				_id: blog._id,
				sys: { ...sys },
				contentfulMetadata: { ...contentfulMetadata },
				title: rest.title,
				description: blogDescription ? `${blogDescription.slice(0, descriptionLimit)}...` : '',
				imageAsset: {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					desktopImage: rest.blogImage,
				} as ImageAsset,
				buttonsCollection: {
					items: [
						{
							__typename: 'Button',
							_id: `${rest._id}-button`,
							sys: { ...sys },
							contentfulMetadata: { ...contentfulMetadata },
							label: 'Read More',
							buttonLink: `/${config.features.blogs.root}/${rest.path ?? ''}/`,
							size: 'extraSmall',
							variant: 'Tertiary High Contrast',
							icon: 'ArrowRightAlt',
							iconPosition: 'end',
						},
					],
					total: 1,
					skip: 0,
					limit: 1,
				},
			};

			return card;
		}),
		limit,
		skip,
		total,
	};
};
